import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createVNode as _createVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 2 }

import { ref, computed, inject, onMounted } from 'vue';
import type { MenuId, MenuItemValue } from '@/venit/services/model/techpubs-cms';
import { ApiMenuReadMenuItemDto, MenuActionType, MenuContentLayout } from '@/venit/services/model/techpubs-cms';
import ApiMenuService from '@/venit/services/api/menu/menu.service';
import ConfirmDialog from '@/venit/components/confirm-dialog/confirm-dialog.vue';
import MenuManagementService from '@/venit/admin/menu-management/menu-management.service';
import AccountService from '@/account/account.service';
import MenuEditDialog from '@/venit/admin/menu-management/menu-management-edit.vue';
import MenuUtils from '@/shared/menu/menu-utils.service';
import { useTranslationStore, useAccountStore } from '@/stores';


export default /*@__PURE__*/_defineComponent({
  __name: 'menu-presenter-content-tree',
  props: {
    rootMenuId: {}
  },
  emits: ["openMenuContent", "openResourcePreview"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

    const menuManagementService = inject<() => MenuManagementService>('menuManagementService')!;
    const menuService = inject<() => ApiMenuService>('menuService')!;
    const accountService = inject<() => AccountService>('accountService')!;

    const translationStore = useTranslationStore();
    const accountStore = useAccountStore();

    const confirmDialog = ref<InstanceType<typeof ConfirmDialog> | null>(null);
    const menuTree = ref<any>(null);

    // Helper functions
    const fileIcon = (mimeType: string | null | undefined): string => {
      if (mimeType?.startsWith('image/')) return 'mdi-file-image-outline';
      if (mimeType === 'application/pdf') return 'mdi-file-pdf-box';
      return 'mdi-file-outline';
    };

    const createVirtualRootMenu = (): ApiMenuReadMenuItemDto => ({
      id: -1,
      name: 'Root menu',
      children: [],
      actionType: MenuActionType.MENU_TEMPLATE_ROOT,
      parentId: null,
      contentLayout: MenuContentLayout.TREE,
      active: true,
      isLeaf: false,
      pathItems: [],
      parentUrlId: null,
      resourceId: null,
      sortNo: 0,
      url: null,
      thumbnailUrl: null,
      totalChildElements: null,
      urlId: null,
      mimeType: null,
    });

    const createEmptyChildMenuItemValue = (): MenuItemValue => ({
      menuId: { id: null },
      version: null,
      urlId: null,
      parentId: null,
      parentUrlId: null,
      name: null,
      isLeaf: null,
      actionType: null,
      contentLayout: MenuContentLayout.CARDS,
      sortNo: 1,
      pathItems: [],
      children: [],
      menuFilters: [],
      active: false,
      thumbnail: {
        uuid: null,
      },
      resourceId: null,
      publicDefault: false,
      translations: [],
    });

    // Reactive state
    const editDialog = ref(false);
    const editedItem = ref<MenuItemValue>(createEmptyChildMenuItemValue());
    const menuItemOpen = ref<ApiMenuReadMenuItemDto[]>([]);
    const virtualRootMenuItem = ref<ApiMenuReadMenuItemDto>(createVirtualRootMenu());
    const menuParents = ref(new Map<number, ApiMenuReadMenuItemDto>());

    // Computed
    const currentLanguage = computed(() => translationStore.currentLanguage);
    const authenticated = computed(() => accountStore.authenticated);
    const isAdmin = computed(() => authenticated.value && accountService().userAuthorities.includes('ROLE_ADMIN'));

    // Methods
    const openMenuContent = (menuId: number | null): MenuId | null => {
      if (menuId == null) {
        emit('openMenuContent', null);
        return null;
      }
      const result = { id: menuId };
      emit('openMenuContent', result);
      return result;
    };

    const onSelectedNode = (selectedNodes: ApiMenuReadMenuItemDto[]) => {
      if (selectedNodes && selectedNodes.length == 1) {
        let oneNode = selectedNodes[0];
        if (MenuUtils.shouldOpenInMainArea(oneNode)) {
          openMenuContent(oneNode.id);
        } else if (MenuUtils.shouldShowAsDialogWithContent(oneNode)) {
          emit('openResourcePreview', oneNode);
        }
      }
    };

    const fixChildrenForTree = (menuItem: ApiMenuReadMenuItemDto) => {
      if (MenuUtils.isTreeMenu(menuItem)) {
        if (!menuItem.children && !menuItem.isLeaf) {
          menuItem.children = [];
        }
      } else {
        menuItem.children = null;
      }
    };

    const updateVirtualRootMenuChildren = async (menuId: MenuId) => {
      const response = isAdmin.value
        ? await menuService().readAdmin(menuId.id, currentLanguage.value, 1, 100)
        : await menuService().read(menuId.id, currentLanguage.value, 1, 100);

      virtualRootMenuItem.value.children.length = 0;
      menuItemOpen.value.length = 0;

      if (response.data.menuItem.id === 0) {
        virtualRootMenuItem.value.children.push(...response.data.menuItem.children);
      } else {
        virtualRootMenuItem.value.children.push(response.data.menuItem);
        response.data.menuItem.children.forEach(child => fixChildrenForTree(child));
      }

      virtualRootMenuItem.value.children.forEach(child => {
        fixChildrenForTree(child);
        menuParents.value.set(child.id, virtualRootMenuItem.value);
      });

      menuItemOpen.value.length = 0;
    };

    const updateMenuChildren = async (item: ApiMenuReadMenuItemDto) => {
      const response = isAdmin.value
        ? await menuService().readAdmin(item.id, currentLanguage.value, 1, 100)
        : await menuService().read(item.id, currentLanguage.value, 1, 100);

      const menu = response.data.menuItem;
      fixChildrenForTree(menu);
      menu.children.forEach(mi => {
        fixChildrenForTree(mi);
        item.children.push(mi);
        menuParents.value.set(mi.id, item);
      });
    };

    const refreshTree = async () => {
      menuItemOpen.value = [];
      await updateVirtualRootMenuChildren(props.rootMenuId);
      openMenuContent(null);
    };

    const deleteMenu = async (itemToRemove: ApiMenuReadMenuItemDto) => {
      if (await confirmDialog.value?.openWithMessage('Delete this item?')) {
        await menuManagementService().deleteMenu({ menuId: { id: itemToRemove.id } });
        await refreshTree();
      }
    };

    const cloneMenu = async (item: ApiMenuReadMenuItemDto) => {
      await menuManagementService().cloneMenu({ menuId: { id: item.id } });
      await refreshTree();
    };

    const loadMenuAndShowEditDialog = async (item: ApiMenuReadMenuItemDto) => {
      const menuValue: MenuItemValue = await menuManagementService().loadMenuValueById({ id: item.id });
      if (menuValue) {
        showEditItemDialog(menuValue);
      }
    };

    const addNewMenuItem = (parentMenu: ApiMenuReadMenuItemDto) => {
      const newMenuOption = createEmptyChildMenuItemValue();
      newMenuOption.parentId = { id: parentMenu.id };
      showEditItemDialog(newMenuOption);
    };

    const showEditItemDialog = (item: MenuItemValue) => {
      editedItem.value = item;
      editDialog.value = true;
    };

    const onCloseMenuItemEditDialog = async (saved: boolean) => {
      closeEditItemDialog();
      if (saved) {
        await refreshTree();
      }
    };

    const closeEditItemDialog = () => {
      editDialog.value = false;
    };

    // Lifecycle
    onMounted(async () => {
      await updateVirtualRootMenuChildren(props.rootMenuId);
    });


return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_treeview = _resolveComponent("v-treeview")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, { fluid: "" }, {
    default: _withCtx(() => [
      false
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 0,
            variant: "outlined",
            onClick: refreshTree
          }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createTextVNode("REFRESH", -1 /* CACHED */)
            ]))]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      _createVNode(_component_v_treeview, {
        density: "compact",
        items: virtualRootMenuItem.value.children,
        "load-children": updateMenuChildren,
        opened: menuItemOpen.value,
        "onUpdate:opened": _cache[0] || (_cache[0] = ($event: any) => ((menuItemOpen).value = $event)),
        "item-value": "id",
        transition: "",
        "return-object": "",
        "open-on-click": "",
        activatable: "",
        ref_key: "menuTree",
        ref: menuTree,
        "onUpdate:activated": onSelectedNode
      }, {
        prepend: _withCtx(({ item, isOpen }) => [
          (_unref(MenuUtils).isTableContent(item))
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 0,
                size: "small"
              }, {
                default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                  _createTextVNode("mdi-table", -1 /* CACHED */)
                ]))]),
                _: 1 /* STABLE */
              }))
            : _createCommentVNode("v-if", true),
          (_unref(MenuUtils).isCardsContent(item))
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 1,
                size: "small"
              }, {
                default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                  _createTextVNode("mdi-view-grid-outline", -1 /* CACHED */)
                ]))]),
                _: 1 /* STABLE */
              }))
            : _createCommentVNode("v-if", true),
          (_unref(MenuUtils).isTreeMenu(item))
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 2,
                size: "small"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(isOpen ? 'mdi-folder-open' : 'mdi-folder'), 1 /* TEXT */)
                ]),
                _: 2 /* DYNAMIC */
              }, 1024 /* DYNAMIC_SLOTS */))
            : _createCommentVNode("v-if", true),
          (item.actionType === _unref(MenuActionType).CORTONA_MODEL)
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 3,
                size: "small"
              }, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode("mdi-rotate-3d", -1 /* CACHED */)
                ]))]),
                _: 1 /* STABLE */
              }))
            : _createCommentVNode("v-if", true),
          (item.actionType === _unref(MenuActionType).FILE)
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 4,
                size: "small"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(fileIcon(item.mimeType)), 1 /* TEXT */)
                ]),
                _: 2 /* DYNAMIC */
              }, 1024 /* DYNAMIC_SLOTS */))
            : _createCommentVNode("v-if", true)
        ]),
        title: _withCtx(({ item }) => [
          (_unref(MenuUtils).isTreeMenu(item))
            ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(item.name), 1 /* TEXT */))
            : _createCommentVNode("v-if", true),
          (_unref(MenuUtils).shouldOpenInMainArea(item))
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(item.name), 1 /* TEXT */))
            : _createCommentVNode("v-if", true),
          (_unref(MenuUtils).shouldShowAsDialogWithContent(item))
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(item.name), 1 /* TEXT */))
            : _createCommentVNode("v-if", true)
        ]),
        append: _withCtx(({ item }) => [
          _createElementVNode("span", null, [
            (isAdmin.value && _unref(MenuUtils).hasEditableChildren(item))
              ? (_openBlock(), _createBlock(_component_v_btn, {
                  key: 0,
                  onClick: _withModifiers(($event: any) => (addNewMenuItem(item)), ["stop"]),
                  icon: "",
                  size: "small",
                  variant: "text",
                  title: "Add a child menu"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { size: "small" }, {
                      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                        _createTextVNode("mdi-plus", -1 /* CACHED */)
                      ]))]),
                      _: 1 /* STABLE */
                    })
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["onClick"]))
              : _createCommentVNode("v-if", true),
            (isAdmin.value && _unref(MenuUtils).isEditableMenu(item))
              ? (_openBlock(), _createBlock(_component_v_btn, {
                  key: 1,
                  onClick: _withModifiers(($event: any) => (loadMenuAndShowEditDialog(item)), ["stop"]),
                  icon: "",
                  size: "small",
                  variant: "text"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { size: "small" }, {
                      default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                        _createTextVNode("mdi-pencil", -1 /* CACHED */)
                      ]))]),
                      _: 1 /* STABLE */
                    })
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["onClick"]))
              : _createCommentVNode("v-if", true),
            (isAdmin.value && _unref(MenuUtils).isEditableMenu(item))
              ? (_openBlock(), _createBlock(_component_v_btn, {
                  key: 2,
                  onClick: _withModifiers(($event: any) => (cloneMenu(item)), ["stop"]),
                  icon: "",
                  size: "small",
                  variant: "text",
                  title: _ctx.$t('admin-menus.tree.clone')
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { size: "small" }, {
                      default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                        _createTextVNode("mdi-content-copy", -1 /* CACHED */)
                      ]))]),
                      _: 1 /* STABLE */
                    })
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["onClick", "title"]))
              : _createCommentVNode("v-if", true),
            (isAdmin.value && _unref(MenuUtils).isEditableMenu(item))
              ? (_openBlock(), _createBlock(_component_v_btn, {
                  key: 3,
                  onClick: _withModifiers(($event: any) => (deleteMenu(item)), ["stop"]),
                  icon: "",
                  size: "small",
                  variant: "text",
                  title: "Delete this menu"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { size: "small" }, {
                      default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                        _createTextVNode("mdi-delete", -1 /* CACHED */)
                      ]))]),
                      _: 1 /* STABLE */
                    })
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["onClick"]))
              : _createCommentVNode("v-if", true)
          ])
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["items", "opened"]),
      _createCommentVNode(" dialog z edycją wybranego elementu "),
      (isAdmin.value)
        ? (_openBlock(), _createBlock(MenuEditDialog, {
            key: 1,
            editDialog: editDialog.value,
            selectedMenuItem: editedItem.value,
            onCloseEditMenuItem: onCloseMenuItemEditDialog
          }, null, 8 /* PROPS */, ["editDialog", "selectedMenuItem"]))
        : _createCommentVNode("v-if", true),
      _createVNode(ConfirmDialog, {
        ref_key: "confirmDialog",
        ref: confirmDialog
      }, null, 512 /* NEED_PATCH */)
    ]),
    _: 1 /* STABLE */
  }))
}
}

})